import React, { useState } from 'react';
import { ChevronRight, AlertCircle, Clock, CheckCircle, Copy, Check, MapPin, WifiOff } from 'lucide-react';
import { copyToClipboard } from '../../../utils/clipboard';
import { formatUserName } from '../../../utils/formatters';
import UserAvatar from '../UserAvatar';

const StatusIcon = ({ status }) => {
    switch (status) {
        case 'pendente':
            return (
                <div className="flex items-center gap-1 text-orange-500 bg-orange-100 dark:bg-orange-900/40 px-2 py-1 rounded-md" title="Pendente" aria-label="Status: Pendente" role="status">
                    <AlertCircle size={16} />
                    <span className="text-xs font-bold uppercase">Pendente</span>
                </div>
            );
        case 'em_execucao':
            return (
                <div className="flex items-center gap-1 text-blue-500 bg-blue-100 dark:bg-blue-900/40 px-2 py-1 rounded-md" title="Em Execução" aria-label="Status: Em Execução" role="status">
                    <Clock size={16} />
                    <span className="text-xs font-bold uppercase">Execução</span>
                </div>
            );
        case 'finalizada':
            return (
                <div className="flex items-center gap-1 text-green-600 bg-green-100 dark:bg-green-900/40 px-2 py-1 rounded-md" title="Finalizada" aria-label="Status: Finalizada" role="status">
                    <CheckCircle size={16} />
                    <span className="text-xs font-bold uppercase">Finalizada</span>
                </div>
            );
        default:
            return null;
    }
};

const NotificationCard = ({ notification, onCardClick, technician }) => {
    const [copySuccess, setCopySuccess] = useState(false);

    const {
        notificationNumber = 'N/A',
        neighborhood = 'Bairro não informado',
        city = 'Cidade não informada',
        address = 'Endereço não informado',
        poleQuantity = 0,
        coordinates = { lat: 0, lng: 0 },
        dueDate,
        status = 'pendente',
        isNetworkless = false,
    } = notification;

    const today = new Date();
    today.setHours(0, 0, 0, 0); // Zera o horário para comparar apenas a data

    // Garante que a data de vencimento seja interpretada no fuso horário local, não em UTC,
    // adicionando 'T00:00:00'. Isso evita o erro de a data aparecer um dia antes.
    const dueDateObj = dueDate ? new Date(dueDate + 'T00:00:00') : null;

    const displayNotificationNumber = String(notificationNumber || 'N/A');

    const timeDiff = dueDateObj ? dueDateObj.getTime() - today.getTime() : 0;
    const daysRemaining = dueDateObj ? Math.ceil(timeDiff / (1000 * 3600 * 24)) : Infinity;
    const needsAttention = status !== 'finalizada' && daysRemaining <= 3;

    const formattedDueDate = dueDateObj
        ? dueDateObj.toLocaleDateString('pt-BR')
        : 'Não definida';

    const handleCopyNumber = async (e) => {
        e.stopPropagation();
        const success = await copyToClipboard(notificationNumber);
        if (success) {
            setCopySuccess(true);
            setTimeout(() => setCopySuccess(false), 2000);
        }
    };

    const handleKeyDown = (e) => {
        // Ignora se o evento se originou de um elemento interativo interno (como o botão de copiar ou link do mapa)
        if (e.target.tagName === 'BUTTON' || e.target.closest('button') || e.target.tagName === 'A' || e.target.closest('a')) {
            return;
        }
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onCardClick(notification);
        }
    };

    return (
        <div
            className="bg-white dark:bg-slate-800/50 rounded-lg shadow-md hover:shadow-lg transition-all duration-300 p-4 flex items-center justify-between border border-transparent cursor-pointer hover:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-slate-900"
            onClick={() => onCardClick(notification)}
            onKeyDown={handleKeyDown}
            role="button"
            tabIndex={0}
            aria-label={`Ver detalhes da notificação ${notificationNumber}`}
        >
            <div className="flex-grow">
                <div className="flex flex-col sm:flex-row sm:items-start justify-between mb-2 gap-2">
                    <div className="flex items-center gap-2 flex-wrap">
                        <div className="flex items-center gap-2">
                            <span className="text-sm font-semibold text-blue-600 dark:text-blue-400 bg-blue-100 dark:bg-blue-900/50 py-1 px-2 rounded whitespace-nowrap" title={displayNotificationNumber}>
                                <span className="md:hidden">N°: {displayNotificationNumber.length > 15 ? `${displayNotificationNumber.substring(0, 15)}...` : displayNotificationNumber}</span>
                                <span className="hidden md:inline">N°: {displayNotificationNumber}</span>
                            </span>
                            <button
                                type="button"
                                onClick={handleCopyNumber}
                                title={copySuccess ? "Número copiado!" : "Copiar Número da Notificação"}
                                aria-label={copySuccess ? "Número copiado!" : "Copiar Número da Notificação"}
                                className="p-1 rounded-full text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors flex-shrink-0 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:focus:ring-offset-slate-900"
                            >
                                {copySuccess ? (
                                    <Check className="h-4 w-4 text-green-500" />
                                ) : (
                                    <Copy className="h-4 w-4" />
                                )}
                            </button>
                        </div>
                        {needsAttention && (
                            <span className="text-xs font-bold text-red-500 bg-red-100 dark:bg-red-900/40 py-1 px-2 rounded animate-pulse uppercase">
                                ATENÇÃO
                            </span>
                        )}
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0 sm:ml-2 mt-1 sm:mt-0">
                        {isNetworkless && (
                            <WifiOff size={20} className="text-red-500" title="Sem Rede" aria-label="Sem Rede" role="img" />
                        )}
                        <StatusIcon status={status} />
                        {(status === 'em_execucao' || status === 'finalizada') && technician && (
                            <div className="flex items-center gap-2" title={`Responsável: ${technician.displayName}`}>
                                <UserAvatar user={technician} size="h-6 w-6" />
                                <span className="text-sm font-medium text-slate-600 dark:text-slate-300 hidden sm:inline">
                                    {formatUserName(technician.displayName)}
                                </span>
                            </div>
                        )}
                    </div>
                </div>

                <h3 className="capitalize text-md font-bold text-slate-800 dark:text-slate-100 mb-1">
                    {(neighborhood || 'Bairro não informado').toLowerCase()}, {(city || 'Cidade não informada').toLowerCase()}
                </h3>

                <p className="text-sm text-slate-500 dark:text-slate-400 mb-3">
                    {address} ({poleQuantity} postes)
                </p>

                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mt-3">
                    <div className="text-sm">
                        {coordinates && coordinates.lat && coordinates.lng ? (
                            <a
                                href={`https://www.google.com/maps/search/?api=1&query=${coordinates.lat},${coordinates.lng}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="inline-flex items-center text-slate-600 dark:text-slate-300 bg-slate-200 dark:bg-slate-700 px-2 py-1 rounded-md hover:bg-slate-300 dark:hover:bg-slate-600 transition-colors"
                                title="Ver no mapa"
                            >
                                <MapPin size={16} className="text-blue-500 dark:text-blue-400 mr-1.5" />
                                <span className="font-mono">
                                    {String(coordinates.lat).substring(0, 7)}, {String(coordinates.lng).substring(0, 7)}
                                </span>
                            </a>
                        ) : (
                            <div className="inline-flex items-center text-slate-500 dark:text-slate-400">
                                <MapPin size={16} className="mr-1.5" />
                                <span>Coordenadas não disponíveis</span>
                            </div>
                        )}
                    </div>
                    <div className="text-sm text-slate-500 dark:text-slate-400 font-mono text-left sm:text-right">
                        Vencimento: {formattedDueDate}
                    </div>
                </div>
            </div>

            <div className="ml-4 flex-shrink-0 self-center">
                <div className="p-2 rounded-full text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors">
                    <ChevronRight size={24} />
                </div>
            </div>
        </div>
    );
};

// Optimized: Wrapped in React.memo with custom comparison to prevent unnecessary re-renders
// when parent component updates and creates new object references for notifications.
const notificationAreEqual = (prevProps, nextProps) => {
    if (prevProps.onCardClick !== nextProps.onCardClick) return false;
    if (prevProps.technician?.uid !== nextProps.technician?.uid) return false;
    if (prevProps.technician?.displayName !== nextProps.technician?.displayName) return false;

    const n1 = prevProps.notification;
    const n2 = nextProps.notification;

    if (!n1 || !n2) return n1 === n2;
    if (n1.id !== n2.id) return false;
    if (n1.updatedAt !== n2.updatedAt) return false;

    // Fallback comparison for visually rendered fields
    if (n1.status !== n2.status) return false;
    if (n1.type !== n2.type) return false;
    if (n1.title !== n2.title) return false;
    if (n1.description !== n2.description) return false;
    if (n1.priority !== n2.priority) return false;
    if (n1.dueDate !== n2.dueDate) return false;
    if (n1.city !== n2.city) return false;

    return true;
};

export default React.memo(NotificationCard, notificationAreEqual);
